import {
	type DocNode,
	type Inline,
	type InlineCardDefinition,
	type TextDefinition,
} from '@atlaskit/adf-schema';
import { reduce } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

/**
 * Extracts mention text from an ADF node
 */
const getMentionText = (node: ADFEntity): string => node.attrs?.text || '@unknown';

/**
 * Extracts URL from an inline/embed card node
 */
const getCardUrl = (node: ADFEntity): string => node.attrs?.url || '';

/**
 * Finds the link mark on a node, if present
 */
const getLinkMark = (node: ADFEntity) => node.marks?.find((mark) => mark.type === 'link');

/**
 * Link mapping for placeholder restoration
 */
export interface LinkMapping {
	[placeholder: string]: {
		url: string;
		text: string;
		type: 'link' | 'smartCard';
	};
}

/**
 * Mention data extracted from ADF
 */
interface MentionData {
	text: string;
	id: string;
	displayName: string;
	localId?: string;
	accessLevel?: string;
}

/**
 * Result from extracting content for autosuggest
 */
export interface AutoSuggestContentExtraction {
	/** Text content with links replaced by placeholders, mentions as plain text */
	processedText: string;
	/** Mapping of placeholders to original link data */
	linkMapping: LinkMapping;
	/** Number of links that were replaced with placeholders */
	linkCount: number;
}

/**
 * Extracts content from ADF for autosuggest, replacing links with placeholders
 * and converting mentions to plain text while maintaining link mapping for restoration.
 *
 * This allows the AI to understand content structure without exposing actual URLs,
 * and enables restoration of links in returned suggestions. Mentions are preserved
 * as plain text for the AI and can be restored from the original ADF.
 *
 * @param adf - The ADF document to process
 * @returns Processed text with link placeholders and link mapping
 */
export const extractContent = (adf: DocNode): AutoSuggestContentExtraction => {
	let linkCounter = 0;

	interface ExtractionState {
		textParts: string[];
		linkMapping: LinkMapping;
		linkCount: number;
	}

	const processADFNode = (currentValue: ExtractionState, node: ADFEntity): ExtractionState => {
		switch (node.type) {
			case 'text':
				// Check if this text has link marks
				const linkMark = getLinkMark(node);

				if (linkMark && linkMark.attrs?.href && node.text) {
					linkCounter++;
					const placeholder = `<link_${linkCounter}>`;
					currentValue.linkMapping[placeholder] = {
						url: linkMark.attrs.href,
						text: node.text,
						type: 'link',
					};
					return {
						...currentValue,
						textParts: [...currentValue.textParts, placeholder],
						linkCount: linkCounter,
					};
				}
				// Regular text
				if (node.text) {
					return {
						...currentValue,
						textParts: [...currentValue.textParts, node.text],
					};
				}
				break;

			case 'inlineCard':
			case 'embedCard':
				// Handle smart cards
				linkCounter++;
				const placeholder = `<link_${linkCounter}>`;
				const cardUrl = getCardUrl(node);
				currentValue.linkMapping[placeholder] = {
					url: cardUrl,
					text: cardUrl,
					type: 'smartCard',
				};
				return {
					...currentValue,
					textParts: [...currentValue.textParts, placeholder],
					linkCount: linkCounter,
				};

			case 'mention':
				// Keep mentions as plain text in the request
				return {
					...currentValue,
					textParts: [...currentValue.textParts, getMentionText(node)],
				};
		}
		return currentValue;
	};

	const initialState: ExtractionState = {
		textParts: [],
		linkMapping: {},
		linkCount: 0,
	};

	const result = reduce<ExtractionState>(adf, processADFNode, initialState);

	return {
		processedText: result.textParts.join(' ').replace(/\s+/g, ' ').trim(),
		linkMapping: result.linkMapping,
		linkCount: result.linkCount,
	};
};

/**
 * Processes autosuggest suggestions by replacing link placeholders with original link information
 * and matching mention text from the original ADF. Creates ADF format when links or mentions are present.
 *
 * @param suggestion - The suggestion text containing link placeholders and mention text
 * @param linkMapping - The mapping of placeholders to original link data
 * @param originalADF - The original ADF document containing mention data
 * @param onValidationFailure - Optional callback to report link placeholder validation errors
 * @returns The suggestion with ADF format if links or mentions are present, plain text if none, or null if validation fails
 */
export const processSuggestion = (
	suggestion: string,
	linkMapping: LinkMapping,
	originalADF?: DocNode | null,
	onValidationFailure?: (invalidData: {
		totalPlaceholders: number;
		invalidCount: number;
		invalidPlaceholders: string[];
	}) => void,
): { text: string; adf: DocNode | null } | null => {
	if (!validateLinks(suggestion, linkMapping, onValidationFailure)) {
		return null;
	}

	// Extract mentions and check if any are in the suggestion
	const mentions = originalADF ? extractMentionsFromADF(originalADF) : [];
	const foundMentions = mentions.filter((mention) => suggestion.includes(mention.text));

	// Check for links and create ADF content
	const hasLinks = /<link_\d+>/.test(suggestion);
	const hasMentionsOrLinks = hasLinks || foundMentions.length > 0;
	const finalContentNodes = hasMentionsOrLinks
		? createADFContent(suggestion, linkMapping, foundMentions)
		: [];

	return {
		text: suggestion,
		adf: hasMentionsOrLinks ? createADFDocument(finalContentNodes) : null,
	};
};

/**
 * Validates link placeholders in suggestion text
 */
const validateLinks = (
	suggestion: string,
	linkMapping: LinkMapping,
	onValidationFailure?: (invalidData: {
		totalPlaceholders: number;
		invalidCount: number;
		invalidPlaceholders: string[];
	}) => void,
): boolean => {
	const placeholdersInSuggestion = suggestion.match(/<link_\d+>/g) || [];

	if (placeholdersInSuggestion.length === 0) {
		return true; // No links to validate
	}

	// Validate placeholders
	const invalidPlaceholders = placeholdersInSuggestion.filter(
		(placeholder) => linkMapping[placeholder] === undefined,
	);

	if (invalidPlaceholders.length > 0) {
		onValidationFailure?.({
			totalPlaceholders: placeholdersInSuggestion.length,
			invalidCount: invalidPlaceholders.length,
			invalidPlaceholders,
		});
		return false; // Validation failed
	}

	return true; // All links are valid
};

/**
 * Creates ADF content with both links and mentions
 */
const createADFContent = (
	suggestion: string,
	linkMapping: LinkMapping,
	mentions: MentionData[],
): Inline[] => {
	const nodes: Inline[] = [];

	// Create regex for placeholders and mentions
	const placeholderKeys = Object.keys(linkMapping);
	const mentionTexts = mentions.map((m) => m.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

	const patterns: string[] = [];
	if (placeholderKeys.length > 0) {
		const escapedPlaceholders = placeholderKeys.map((p) =>
			p.replace(/[<>[\]{}()*+?.,\\^$|#\s]/g, '\\$&'),
		);
		patterns.push(`(${escapedPlaceholders.join('|')})`);
	}
	if (mentionTexts.length > 0) {
		patterns.push(`(${mentionTexts.join('|')})`);
	}

	if (patterns.length === 0) {
		return [{ type: 'text', text: suggestion }];
	}

	const combinedRegex = new RegExp(patterns.join('|'), 'g');
	let lastIndex = 0;
	let match;

	while ((match = combinedRegex.exec(suggestion)) !== null) {
		const matchedText = match[0];
		const matchIndex = match.index;

		// Add text before the match
		if (matchIndex > lastIndex) {
			const textBefore = suggestion.substring(lastIndex, matchIndex);
			nodes.push({ type: 'text', text: textBefore });
		}

		// Determine if it's a link placeholder or mention
		if (matchedText.startsWith('<link_')) {
			// Handle link placeholder
			const linkData = linkMapping[matchedText];
			if (linkData) {
				if (linkData.type === 'smartCard') {
					nodes.push({
						type: 'inlineCard',
						attrs: { url: linkData.url },
					} as InlineCardDefinition);
				} else {
					nodes.push({
						type: 'text',
						text: linkData.text,
						marks: [{ type: 'link', attrs: { href: linkData.url } }],
					} as TextDefinition);
				}
			}
		} else {
			// Handle mention
			const mentionData = mentions.find((m) => m.text === matchedText);
			if (mentionData) {
				nodes.push({
					type: 'mention',
					attrs: {
						id: mentionData.id,
						text: matchedText,
						displayName: mentionData.displayName,
						...(mentionData.localId && { localId: mentionData.localId }),
						...(mentionData.accessLevel && { accessLevel: mentionData.accessLevel }),
					},
				} as Inline);
			}
		}

		lastIndex = matchIndex + matchedText.length;
	}

	// Add remaining text after the last match
	if (lastIndex < suggestion.length) {
		const textAfter = suggestion.substring(lastIndex);
		nodes.push({ type: 'text', text: textAfter });
	}

	return nodes.length > 0 ? nodes : [{ type: 'text', text: suggestion }];
};

/**
 * Creates an ADF document with given content nodes
 */
const createADFDocument = (contentNodes: Inline[]): DocNode => ({
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: contentNodes,
		},
	],
});

/**
 * Extracts all mention data from an ADF document
 *
 * @param adf - The ADF document to extract mentions from
 * @returns Array of mention data found in the document
 */
const extractMentionsFromADF = (adf: DocNode): MentionData[] => {
	const mentions: MentionData[] = [];

	const extractMentions = (node: ADFEntity | undefined) => {
		if (!node) {
			return;
		}

		if (node.type === 'mention') {
			mentions.push({
				text: node.attrs?.text || '',
				id: node.attrs?.id || '',
				displayName: node.attrs?.displayName || node.attrs?.text || '',
				localId: node.attrs?.localId,
				accessLevel: node.attrs?.accessLevel,
			});
		}

		if (node.content && Array.isArray(node.content)) {
			node.content.forEach(extractMentions);
		}
	};

	extractMentions(adf);
	return mentions;
};

/**
 * Extracts plain text content from an ADF document for comparison purposes.
 * This extracts readable text from text nodes and mentions, ignoring structure.
 *
 * @param adf - The ADF document to extract text from
 * @returns Plain text content of the document
 */
export const extractPlainText = (adf: DocNode): string => {
	const extractTextFromNode = (parts: string[], node: ADFEntity): string[] => {
		switch (node.type) {
			case 'text':
				if (node.text) {
					return [...parts, node.text];
				}
				break;
			case 'inlineCard':
			case 'embedCard':
				// For cards, use the URL as representative text
				return [...parts, getCardUrl(node)];
			case 'mention':
				return [...parts, getMentionText(node)];
		}
		return parts;
	};

	const textParts = reduce<string[]>(adf, extractTextFromNode, []);
	return textParts.join('');
};

/**
 * Creates a copy of an ADF document with bold marks added to the completion portion.
 * The completion is determined by finding where the query text ends in the suggestion.
 *
 * @param suggestionAdf - The suggestion ADF to modify
 * @param queryAdf - The query ADF (optional)
 * @param queryText - The query plain text (optional, used if queryAdf not provided)
 * @returns A new ADF document with bold marks on the completion portion, or null if no modification needed
 */
export const createBoldedCompletionADF = (
	suggestionAdf: DocNode,
	queryAdf?: DocNode,
	queryText?: string,
): DocNode | null => {
	// Get the query text
	let normalizedQuery = '';
	if (queryAdf) {
		normalizedQuery = extractPlainText(queryAdf).toLowerCase().trim();
	} else if (queryText) {
		normalizedQuery = queryText.toLowerCase().trim();
	}

	if (!normalizedQuery) {
		return null;
	}

	// Get suggestion text and find where query ends
	const suggestionText = extractPlainText(suggestionAdf);
	const normalizedSuggestion = suggestionText.toLowerCase();
	const matchIndex = normalizedSuggestion.indexOf(normalizedQuery);

	if (matchIndex === -1) {
		// Query not found, bold everything
		return addBoldMarksFromPosition(suggestionAdf, 0);
	}

	// Calculate where the completion starts
	const completionStartPos = matchIndex + normalizedQuery.length;

	if (completionStartPos >= suggestionText.length) {
		// No completion portion to bold
		return null;
	}

	return addBoldMarksFromPosition(suggestionAdf, completionStartPos);
};

/**
 * Adds bold (strong) marks to text nodes starting from a character position.
 */
const addBoldMarksFromPosition = (adf: DocNode, startPosition: number): DocNode => {
	let currentPosition = 0;

	const processNode = (node: ADFEntity): ADFEntity => {
		if (node.type === 'text' && node.text) {
			const nodeStartPos = currentPosition;
			const nodeEndPos = currentPosition + node.text.length;
			currentPosition = nodeEndPos;

			// Check if this node is entirely before the bold start position
			if (nodeEndPos <= startPosition) {
				return node;
			}

			// Check if bold starts within this node
			if (nodeStartPos < startPosition && nodeEndPos > startPosition) {
				// Need to split this node - but since we're returning a single node,
				// we'll just bold the whole node if any part should be bold
				// For simplicity, we bold the entire node
				const existingMarks = node.marks || [];
				const hasStrong = existingMarks.some((mark) => mark.type === 'strong');
				if (!hasStrong) {
					return {
						...node,
						marks: [...existingMarks, { type: 'strong' }],
					} as ADFEntity;
				}
				return node;
			}

			// Node is entirely in the bold region
			if (nodeStartPos >= startPosition) {
				const existingMarks = node.marks || [];
				const hasStrong = existingMarks.some((mark) => mark.type === 'strong');
				if (!hasStrong) {
					return {
						...node,
						marks: [...existingMarks, { type: 'strong' }],
					} as ADFEntity;
				}
			}

			return node;
		}

		// Track position for non-text inline content
		if (node.type === 'inlineCard' || node.type === 'embedCard') {
			currentPosition += getCardUrl(node).length;
			return node;
		}

		if (node.type === 'mention') {
			currentPosition += getMentionText(node).length;
			return node;
		}

		// For container nodes, process children
		if (node.content && Array.isArray(node.content)) {
			return {
				...node,
				content: node.content
					.filter((child): child is ADFEntity => child !== undefined)
					.map(processNode),
			} as ADFEntity;
		}

		return node;
	};

	return processNode(adf) as DocNode;
};

/**
 * Calculates the text content length from an ADF document as it would be sent to the backend
 * (counting placeholder lengths for links/smart cards, not actual URLs)
 *
 * @param adf - The ADF document to measure
 * @returns The length of the processed text content with placeholders
 */
export const getADFTextLength = (adf: DocNode): number => {
	// Placeholder length is always 8 characters: "<link_1>", "<link_2>", etc.
	const PLACEHOLDER_LENGTH = 8;

	const calculateNodeLength = (currentLength: number, node: ADFEntity): number => {
		switch (node.type) {
			case 'text':
				// Link
				const linkMark = getLinkMark(node);

				if (linkMark && linkMark.attrs?.href && node.text) {
					// Count placeholder length instead of actual link text
					return currentLength + PLACEHOLDER_LENGTH;
				}

				// Regular text
				return currentLength + (node.text?.length || 0);

			case 'inlineCard':
			case 'embedCard':
				// Count placeholder length instead of URL length
				return currentLength + PLACEHOLDER_LENGTH;

			case 'mention':
				return currentLength + getMentionText(node).length;

			default:
				// For other node types, don't add to length
				return currentLength;
		}
	};

	return reduce<number>(adf, calculateNodeLength, 0);
};

/**
 * Extracts the completion part of a suggestion that extends beyond the original query.
 * Returns the prefix (matching query part) and completion (new text to be bolded).
 *
 * @param suggestionText - The full suggestion text
 * @param query - The original query string (optional)
 * @param queryAdf - The original query in ADF format (optional)
 * @returns Object with prefix and completion, or null if no match found
 */
export const extractSuggestionCompletion = (
	suggestionText: string,
	query?: string,
	queryAdf?: DocNode,
): { prefix: string; completion: string } | null => {
	// Get the query text - prefer ADF extraction if available
	let queryText = '';
	if (queryAdf) {
		const { processedText } = extractContent(queryAdf);
		queryText = processedText;
	} else if (query) {
		queryText = query;
	}

	if (!queryText) {
		return null;
	}

	// Normalize both strings for comparison (lowercase, trim whitespace)
	const normalizedQuery = queryText.toLowerCase().trim();
	const normalizedSuggestion = suggestionText.toLowerCase();

	// Find where the query appears in the suggestion (case-insensitive)
	const matchIndex = normalizedSuggestion.indexOf(normalizedQuery);

	if (matchIndex === -1) {
		// Query not found in suggestion - return entire suggestion as completion
		return { prefix: '', completion: suggestionText };
	}

	// Extract the parts using original case from suggestion
	const prefixEnd = matchIndex + queryText.length;
	const prefix = suggestionText.substring(0, prefixEnd);
	const completion = suggestionText.substring(prefixEnd);

	return { prefix, completion };
};
